<template>
  <!-- vehicle details card -->
  <v-card flat class="pa-5 bg-pwt_snow">
    <!-- wof/cof expired band, styled after #unread_notifications_bar in NotificationsContent.vue -->
    <div v-if="wofCofExpired" id="wof_cof_expired_band">
      <v-icon color="error" class="mr-2"> mdi-alert-circle-outline </v-icon>
      <span>WOF/COF has expired for this vehicle</span>
    </div>
    <!-- location stale band, styled after wof_cof_expired_band above -->
    <div v-if="locationIsStale" id="location_stale_band">
      <v-icon color="error" class="mr-2"> mdi-map-marker-off-outline </v-icon>
      <span>This vehicle hasn't reported its location recently - its map position may be out of date</span>
    </div>
    <!-- RUC overdue/coming due bands, styled after wof_cof_expired_band above.
         Never both at once (see getVehicleRucFlags), and never shown at all
         for a petrol/hybrid vehicle - see rucAppliesToday. -->
    <div v-if="vehicleRucFlags.isOverdue" id="ruc_overdue_band">
      <v-icon color="error" class="mr-2"> mdi-alert-circle-outline </v-icon>
      <span>RUC has expired for this vehicle</span>
    </div>
    <div v-else-if="vehicleRucFlags.isComingDue" id="ruc_coming_due_band">
      <v-icon color="warning" class="mr-2"> mdi-alert-outline </v-icon>
      <span>RUC is due within {{ RUC_COMING_DUE_THRESHOLD_KM }} km for this vehicle</span>
    </div>
    <!-- vehicle details card -->
    <v-card class="py-4 pl-md-12 rounded-lg bg-pwt_snow mb-10" flat>
      <v-row class="flex-column flex-md-row" align="center">
        <v-col md="3">
          <!-- image section -->
          <v-skeleton-loader
            type="image"
            width="100%"
            height="140px"
            v-if="!vehicle"
          />
          <v-img
            v-else
            contain
            :src="imageSrc()"
            aspect-ratio="1"
            :width="mdAndUp ? '100%' : '40%'"
            :height="mdAndUp ? '100%' : 'auto'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <v-col md="9">
          <div v-if="vehicle" class="d-flex flex-nowrap align-center">
            <div class="flex-grow-1 overflow-hidden">
              <div class="text-h6 text-truncate">
                {{
                  vehicle.vehicleSource === "GENERIC"
                    ? "Generic EV"
                    : (vehicle.licensePlate ?? "unrecorded license plate")
                }}
              </div>
              <div class="font-weight-medium text-truncate">
                {{ vehicle.name ?? "unnamed vehicle" }}
              </div>
              <div
                v-if="homeLocation"
                class="text-body-2 text-medium-emphasis text-truncate"
              >
                <v-icon icon="mdi-garage" size="16" class="mr-1" />
                {{ homeLocation.name || homeLocation.address }}
              </div>
            </div>
            <CustomOutlinedButton class="ml-2 mr-2 flex-shrink-0" @click="showBookingDialog = true">
              Book
            </CustomOutlinedButton>
            <v-menu location="start" offset="-45 -50">
              <template v-slot:activator="{ props }">
                <v-btn
                  flat
                  color="transparent"
                  v-bind="props"
                  icon="mdi-dots-horizontal"
                  class="text-primary flex-shrink-0"
                />
              </template>
              <v-list>
                <v-list-item
                  title="Set as default trip vehicle"
                  @click="isSelected ? handleSelectOther() : handleSelect()"
                />
                <v-list-item
                  title="Edit details"
                  @click="toEditVehiclePage"
                />
                <v-list-item
                  v-if="hasMapLocation"
                  title="View on map"
                  @click="handleViewOnMap"
                />
                <v-list-item title="Make a copy" class="text-gray400" />
                <v-list-item title="Delete" class="text-error" />
              </v-list>
            </v-menu>
          </div>
          <div v-else>
            <v-row no-gutters class="mb-4">
              <v-skeleton-loader type="heading" width="70%" />
              <v-skeleton-loader type="heading" width="30%" />
            </v-row>
            <v-skeleton-loader type="text" width="40%" />
            <v-skeleton-loader type="text" width="70%" class="mb-4" />
            <v-skeleton-loader type="heading" width="100%" />
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Details section -->
    <v-card class="py-4 px-8 rounded-lg mb-10 bg-pwt_snow" elevation="1">
      <v-card-title> Details section </v-card-title>
      <v-card-text>
        <div
          v-if="makeModelYearText"
          class="pa-2 bg-background d-flex rounded mb-3"
        >
          <v-icon icon="mdi-information-outline" class="mr-3" color="primary" />
          {{ makeModelYearText }}
        </div>
        <div class="pa-2 bg-background d-flex rounded mb-3">
          <v-icon :icon="fuelIcon" class="mr-3" color="primary" />
          {{ fuelType }}
        </div>
        <div
          v-if="latestOdometerKm !== undefined"
          class="pa-2 bg-background d-flex rounded mb-3"
        >
          <v-icon icon="mdi-speedometer" class="mr-3" color="primary" />
          {{ formatKm(latestOdometerKm) }} km on the odometer
        </div>
        <div
          v-if="homeLocation"
          class="pa-2 bg-background d-flex rounded mb-3 cursor-pointer"
          @click="handleViewHomeLocation"
        >
          <v-icon icon="mdi-garage" class="mr-3" color="primary" />
          Home location: {{ homeLocation.name || homeLocation.address }}
        </div>
      </v-card-text>

      <v-card-title> Additional load capacity </v-card-title>
      <v-card-text>
        <div class="pa-2 bg-background d-flex rounded mb-3">
          <v-icon icon="custom:rvHookup" class="mr-3" color="primary" />
          Can carry upto {{ additionalWeight }}kg
        </div>
        <div
          v-if="bootCapacityText"
          class="pa-2 bg-background d-flex rounded mb-3"
        >
          <v-icon
            icon="mdi-package-variant-closed"
            class="mr-3"
            color="primary"
          />
          {{ bootCapacityText }}
        </div>
      </v-card-text>
    </v-card>

    <!-- RUC status section -->
    <v-card class="py-4 px-8 rounded-lg mb-10" elevation="1">
      <v-card-title> RUC status </v-card-title>
      <v-card-text v-if="!rucAppliesToday" class="text-caption text-medium-emphasis pt-0">
        This vehicle pays fuel excise duty at the pump, not RUC - shown here
        for reference only, ahead of the planned 2027/28 RUC extension to
        light petrol/hybrid vehicles.
      </v-card-text>
      <v-card-text
        v-if="vehicleRucRate !== undefined"
        class="text-caption text-medium-emphasis pt-0"
      >
        Current rate: ${{ formatCurrency(vehicleRucRate) }} per 1,000 km
      </v-card-text>
      <v-card-text v-if="rucStore.isFetching">
        Loading RUC status...
      </v-card-text>
      <v-card-text v-else-if="!vehicleRucStatus">
        No RUC license on file for this vehicle.
      </v-card-text>
      <v-list v-else>
        <v-list-item
          class="rounded-lg"
          :title="`License ${vehicleRucStatus.license_number ?? 'unknown'}`"
          :subtitle="`Covers to ${formatKm(vehicleRucStatus.distance_end_km)} km · odometer ${formatKm(vehicleRucStatus.latest_odometer_km)} km · ${rucKmRemainingText}`"
          :style="rucItemStyle"
        />
      </v-list>
    </v-card>

    <!-- RUC purchase history section -->
    <v-card class="py-4 px-8 rounded-lg mb-10" elevation="1">
      <v-card-title> RUC purchase history </v-card-title>
      <v-card-text v-if="rucStore.isFetching">
        Loading RUC purchase history...
      </v-card-text>
      <v-card-text v-else-if="!vehicleRucLicenses.length">
        No RUC purchase history for this vehicle.
      </v-card-text>
      <v-list v-else>
        <v-list-item
          class="rounded-lg mb-1"
          v-for="license in vehicleRucLicenses"
          :key="license.id"
          :title="`License ${license.license_number ?? 'unknown'} — $${formatCurrency(license.amount_paid)}`"
          :subtitle="`Purchased ${getNiceDate(license.purchased_at)} · ${formatKm(license.distance_end_km - license.distance_start_km)} km block (${formatKm(license.distance_start_km)}–${formatKm(license.distance_end_km)} km)`"
        />
      </v-list>
    </v-card>

    <!-- Inspection history section (rough scaffold, not final UI) -->
    <v-card class="py-4 px-8 rounded-lg mb-10" elevation="1">
      <v-card-title> Compliance status </v-card-title>
      <v-card-text v-if="inspectionHistoryStore.isFetching">
        Loading inspection history...
      </v-card-text>
      <v-card-text
        v-else-if="!vehicleInspectionHistory?.complianceStatus.length"
      >
        No inspection records for this vehicle.
      </v-card-text>
      <template v-else>
        <v-list v-if="vehicleInspectionHistory.complianceStatus.length">
          <v-list-item
            class="rounded-lg"
            v-for="status in vehicleInspectionHistory.complianceStatus"
            :key="status.id"
            :title="`WOF/COF: ${status.wof_cof_status ?? 'unknown'}`"
            :subtitle="`Registration: ${status.registration_status ?? 'unknown'} · next inspection due ${status.next_inspection_due ? getNiceDate(status.next_inspection_due) : 'unknown'}`"
            :style="isCompliancePastDue(status) ? failedItemStyle : undefined"
          />
        </v-list>
        <v-card-text v-else> No compliance status recorded. </v-card-text>
      </template>
    </v-card>

    <v-card class="py-4 px-8 rounded-lg mb-10" elevation="1">
      <v-card-title> Inspection history </v-card-title>
      <v-card-text v-if="inspectionHistoryStore.isFetching">
        Loading inspection history...
      </v-card-text>
      <v-card-text
        v-else-if="!vehicleInspectionHistory?.inspectionRecords.length"
      >
        No inspection records for this vehicle.
      </v-card-text>
      <v-expansion-panels
        v-else
        flat
        variant="default"
        class="inspection-history-panels"
      >
        <v-expansion-panel
          v-for="record in vehicleInspectionHistory.inspectionRecords"
          :key="record.id"
        >
          <v-expansion-panel-title
            class="rounded-lg"
            :style="getRecordResultStyle(record)"
          >
            <div>
              <div>
                {{ record.inspection_type }} — {{ record.initial_result }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ getNiceDate(record.initial_inspection_date) }} · odometer
                {{ record.odometer_km ? formatKm(record.odometer_km) : "unknown" }} km
              </div>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list
              v-if="
                inspectionHistoryStore.getBrakeTestsForRecord(record.id).length
              "
              density="compact"
            >
              <v-list-subheader>Brake tests</v-list-subheader>
              <v-list-item
                v-for="test in inspectionHistoryStore.getBrakeTestsForRecord(
                  record.id,
                )"
                class="rounded-lg mb-1"
                :key="test.id"
                :title="`${test.brake_type} (${test.test_phase}) — ${test.result ?? 'unrecorded'}`"
                :subtitle="`${test.percentage_value ?? 'unknown'}% · weight reading ${test.weight_reading ?? 'unknown'}`"
                :style="
                  isFailedResult(test.result) ? failedItemStyle : undefined
                "
              />
            </v-list>

            <v-list
              v-if="
                inspectionHistoryStore.getLightingChecksForRecord(record.id)
                  .length
              "
              density="compact"
            >
              <v-list-subheader>Lighting checks</v-list-subheader>
              <v-list-item
                v-for="check in inspectionHistoryStore.getLightingChecksForRecord(
                  record.id,
                )"
                class="rounded-lg mb-1"
                :key="check.id"
                :title="`${check.light_type} (${check.test_phase}) — ${check.result}`"
                :style="
                  isFailedResult(check.result) ? failedItemStyle : undefined
                "
              />
            </v-list>

            <v-list
              v-if="
                inspectionHistoryStore.getTyreTestsForRecord(record.id).length
              "
              density="compact"
            >
              <v-list-subheader>Tyre tests</v-list-subheader>
              <v-list-item
                v-for="test in inspectionHistoryStore.getTyreTestsForRecord(
                  record.id,
                )"
                class="rounded-lg mb-1"
                :key="test.id"
                :title="`${test.axle_position} ${test.side} (${test.test_phase}) — ${test.result ?? 'unrecorded'}`"
                :subtitle="`tread ${test.tread_score ?? 'unknown'} · weight ${test.weight_percentage ?? 'unknown'}% · imbalance ${test.imbalance_percentage ?? 'unknown'}%`"
                :style="
                  isFailedResult(test.result) ? failedItemStyle : undefined
                "
              />
            </v-list>

            <v-list
              v-if="
                inspectionHistoryStore.getVehicleConditionChecksForRecord(
                  record.id,
                ).length
              "
              density="compact"
            >
              <v-list-subheader>Condition checks</v-list-subheader>
              <v-list-item
                v-for="check in inspectionHistoryStore.getVehicleConditionChecksForRecord(
                  record.id,
                )"
                :key="check.id"
                class="rounded-lg mb-1"
                :title="`${check.check_area} (${check.test_phase}) — ${check.result}`"
                :subtitle="check.notes ?? undefined"
                :style="
                  isFailedResult(check.result) ? failedItemStyle : undefined
                "
              />
            </v-list>

            <div
              v-if="
                !inspectionHistoryStore.getBrakeTestsForRecord(record.id)
                  .length &&
                !inspectionHistoryStore.getLightingChecksForRecord(record.id)
                  .length &&
                !inspectionHistoryStore.getTyreTestsForRecord(record.id)
                  .length &&
                !inspectionHistoryStore.getVehicleConditionChecksForRecord(
                  record.id,
                ).length
              "
              class="text-medium-emphasis"
            >
              No checklist detail recorded for this inspection.
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <GenericDialog
      :activator="showVehicleGalleryDialog"
      @close="showVehicleGalleryDialog = false"
    >
      <VehicleSearchContent
        @vehicle-select="handleVehicleSelect"
        show-generic
      />
    </GenericDialog>

    <BookingPremiumFeatureDialog
      :activator="showBookingDialog"
      @close="showBookingDialog = false"
      title="Book vehicle"
    />
  </v-card>
</template>

<script lang="ts" setup>
import BookingPremiumFeatureDialog from "@/components/dialog-wrappers/BookingPremiumFeatureDialog.vue";
import GenericDialog from "@/components/dialog-wrappers/GenericDialog.vue";
import CustomOutlinedButton from "@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import VehicleSearchContent from "@/components/vehicles/VehicleSearchContent.vue";
import Coordinate from "@/domains/coordinates/coordinate";
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import type { InspectionRecord } from "@/domains/inspectionHistory/types";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore";
import { useRucStore } from "@/domains/ruc";
import { useUserStore } from "@/domains/user/userStore.ts";
import { useVehicleStore } from "@/domains/vehicles";
import type { Vehicle } from "@/domains/vehicles/types.ts";
import { getVehicleLocationFreshness } from "@/domains/vehicles/vehicleLocationFreshness";
import {
  getVehicleRucFlags,
  RUC_COMING_DUE_THRESHOLD_KM,
  vehiclePaysRucToday,
} from "@/domains/vehicles/vehicleRucStatus";
import { useDashboardNavStore } from "@/stores/dashboardNavStore.ts";
import { useMapStore } from "@/stores/mapStore";
import { capitalizeFirstLetter } from "@/utils/stringUtils";
import { getNiceDate } from "@/utils/timeUtils";
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";

const dashboardNavStore = useDashboardNavStore();
const toEditVehiclePage = () => {
  dashboardNavStore.displayedContent = "vehicle-edit-details";
};

const { vehicle, isSelected = false } = defineProps<{
  vehicle?: Vehicle | undefined;
  isSelected?: boolean;
}>();

const { mdAndUp } = useDisplay();

const vehicleStore = useVehicleStore();
const savedLocationStore = useSavedLocationStore();
const mapStore = useMapStore();

/** This vehicle's last recorded location, if it has telematics data at all. */
const vehicleLocation = computed(() =>
  vehicle?.externalId
    ? vehicleStore.getLocationForVehicle(vehicle.externalId)
    : undefined,
);

/** Whether this vehicle has a recorded location to fly the map to. */
const hasMapLocation = computed(() => !!vehicleLocation.value);

/**
 * True once this vehicle's location hasn't updated in
 * LOCATION_STALE_THRESHOLD_MINUTES - the map marker greys out at the same
 * point (see mapStore's use of getVehicleLocationFreshness).
 */
const locationIsStale = computed(
  () =>
    !!vehicleLocation.value &&
    getVehicleLocationFreshness(vehicleLocation.value) === "stale",
);

/**
 * Starts the map's fly-to animation for this vehicle's last recorded
 * location and closes the dashboard so the map underneath is visible.
 */
function handleViewOnMap(): void {
  if (
    !vehicleLocation.value ||
    vehicleLocation.value.latitude == null ||
    vehicleLocation.value.longitude == null
  )
    return;

  mapStore.flyTo = new Coordinate({
    latitude: vehicleLocation.value.latitude,
    longitude: vehicleLocation.value.longitude,
  });
  dashboardNavStore.closeDashboard();
}

/** This vehicle's assigned home/depot location, if one is set. */
const homeLocation = computed(() =>
  vehicle?.homeLocationExternalId
    ? savedLocationStore.getLocationByExternalId(vehicle.homeLocationExternalId)
    : undefined,
);

function handleViewHomeLocation(): void {
  if (!homeLocation.value) return;
  dashboardNavStore.showLocationDetails(homeLocation.value.localId);
}

// weights
// TODO: no data source for this yet - see additionalWeight's previous
// ref-based stub, which was likewise always 0.
const additionalWeight = computed(() => 0);

/**
 * The vehicle's boot capacity as display text, derived from its EPA fuel
 * efficiency rating's luggage volume (see vehicleStore.getBootCapacityTextForVehicle).
 * Undefined when no rating has been resolved/fetched, or it has no luggage
 * volume recorded.
 */
const bootCapacityText = computed(() =>
  vehicle?.externalId
    ? vehicleStore.getBootCapacityTextForVehicle(vehicle.externalId)
    : undefined,
);

// fuel
const fuelType = computed<string>(() =>
  capitalizeFirstLetter(vehicle?.fuelType ?? "DIESEL"),
);
const fuelIcon = computed<string | undefined>(() => vehicle?.fuelTypeIcon);

/**
 * "{make} {model} ({year})", derived from the vehicle's EPA fuel efficiency
 * rating. Undefined when no rating has been resolved/fetched for this
 * vehicle.
 */
const makeModelYearText = computed(() => {
  const rating = vehicle?.externalId
    ? vehicleStore.getEpaFuelEfficiencyRatingForVehicle(vehicle.externalId)
    : undefined;

  return rating
    ? `${rating.make} ${rating.model} (${rating.model_year})`
    : undefined;
});

// Display helpers

const inspectionHistoryStore = useInspectionHistoryStore();
const rucStore = useRucStore();

/**
 * This vehicle's current RUC status, looked up by its external id.
 *
 * `fetchRuc` is expected to already have been called during app bootstrap
 * (see `appStore`) — this is a pure read against store state, not a fetch
 * trigger. `undefined` means the vehicle has no RUC license on file, not a
 * loading state.
 */
const vehicleRucStatus = computed(() =>
  vehicle?.externalId
    ? rucStore.getStatusForVehicle(vehicle.externalId)
    : undefined,
);

/** Formats a km figure with thousands separators, e.g. 45231 -> "45,231". */
function formatKm(value: number): string {
  // Coerced via Number() rather than trusted as-is - a numeric string (seen
  // with some mock/simulated data, and possibly real API responses given
  // today's backend JSON issue) has its own toLocaleString() that doesn't
  // do numeric grouping at all, so it would silently print unformatted.
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString()
    : String(value);
}

/** Formats a dollar figure with thousands separators and exactly 2 decimal places, e.g. 1234.5 -> "1,234.50". */
function formatCurrency(value: number): string {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : String(value);
}

/** "overrun by Xkm" once past the license's covered distance, else "Xkm remaining". */
const rucKmRemainingText = computed(() => {
  const status = vehicleRucStatus.value;
  if (!status) return "";

  return status.km_remaining < 0
    ? `overrun by ${formatKm(Math.abs(status.km_remaining))} km`
    : `${formatKm(status.km_remaining)} km remaining`;
});

/**
 * This vehicle's most recently-purchased usable RUC rate (NZD/1000km), or
 * `undefined` if it has no license with a usable rate. Independent of
 * `vehicleRucStatus` - it's derived from purchase history, not the current
 * active-license RPC, so it can still resolve even when the vehicle has no
 * currently-active license.
 */
const vehicleRucRate = computed(() =>
  vehicle?.externalId
    ? rucStore.getLatestRateForVehicle(vehicle.externalId)
    : undefined,
);

/**
 * True if this vehicle actually pays RUC today (see vehiclePaysRucToday) -
 * petrol/hybrid vehicles pay fuel excise duty at the pump instead, so their
 * RUC status shouldn't drive alerts or item highlighting, and the RUC status
 * card shows an explanatory caveat instead.
 */
const rucAppliesToday = computed(() => vehiclePaysRucToday(vehicle?.fuelType));

/**
 * This vehicle's RUC overdue/coming-due classification, already accounting
 * for rucAppliesToday - both flags come back false for a petrol/hybrid
 * vehicle regardless of its RucStatus row.
 */
const vehicleRucFlags = computed(() =>
  getVehicleRucFlags(vehicleRucStatus.value, vehicle?.fuelType),
);

/** Highlights the RUC status row the same way compliance/inspection rows are highlighted. */
const rucItemStyle = computed(() => {
  if (vehicleRucFlags.value.isOverdue) return failedItemStyle;
  if (vehicleRucFlags.value.isComingDue) return comingDueItemStyle;
  return undefined;
});

/**
 * This vehicle's RUC purchase history, newest purchase first.
 *
 * Sorted by `purchased_at` (the real-world purchase date), not `created_at`
 * - `created_at` only reflects when the row was written/synced to us, which
 * doesn't necessarily match when the license was actually purchased.
 */
const vehicleRucLicenses = computed(() => {
  if (!vehicle?.externalId) return [];

  return [...rucStore.getLicensesForVehicle(vehicle.externalId)].sort(
    (a, b) =>
      new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime(),
  );
});

/**
 * The vehicle's full inspection history, looked up by its external id.
 *
 * `fetchInspectionHistory` is expected to already have been called during
 * app bootstrap (see `appStore`) — this is a pure read against store state,
 * not a fetch trigger.
 */
const vehicleInspectionHistory = computed(() =>
  vehicle?.externalId
    ? inspectionHistoryStore.getFullHistoryForVehicle(vehicle.externalId)
    : null,
);

/**
 * The vehicle's most recently recorded odometer reading, from inspection
 * history - the newest record (by initial_inspection_date) that actually
 * has one, since not every inspection record captures it. `undefined` when
 * no record has an odometer reading at all, not just when there are no
 * records.
 */
const latestOdometerKm = computed(() => {
  const recordsWithOdometer = (
    vehicleInspectionHistory.value?.inspectionRecords ?? []
  ).filter(
    (record): record is InspectionRecord & { odometer_km: number } =>
      record.odometer_km != null,
  );

  const sorted = [...recordsWithOdometer].sort(
    (a, b) =>
      new Date(b.initial_inspection_date).getTime() -
      new Date(a.initial_inspection_date).getTime(),
  );

  return sorted[0]?.odometer_km;
});

/**
 * True if any of the vehicle's compliance status rows indicate an expired
 * WOF/COF — either the backend's own `days_until_due` has gone negative, or
 * `wof_cof_status` says so directly (checked as a fallback since we don't
 * have a confirmed enum for that column's values).
 */
const wofCofExpired = computed(() =>
  (vehicleInspectionHistory.value?.complianceStatus ?? []).some(
    (status) =>
      (status.days_until_due ?? 0) < 0 ||
      status.wof_cof_status?.toUpperCase().includes("EXPIRED"),
  ),
);

/**
 * True if this individual compliance status row is itself overdue for
 * WOF/COF or registration - same fields as wofCofExpired above, but judged
 * per-row rather than fleet/vehicle-wide, so the failing row can be
 * highlighted in the compliance status list.
 */
function isCompliancePastDue(status: {
  days_until_due: number | null;
  wof_cof_status: string | null;
  registration_status: string | null;
}): boolean {
  return (
    (status.days_until_due ?? 0) < 0 ||
    !!status.wof_cof_status?.toUpperCase().includes("EXPIRED") ||
    !!status.registration_status?.toUpperCase().includes("EXPIRED")
  );
}

/** True if a check/test result string indicates a failure. */
function isFailedResult(result: string | null | undefined): boolean {
  return !!result?.toUpperCase().includes("FAIL");
}

/** True if any of the record's nested brake/lighting/tyre/condition checks failed. */
function hasFailedTest(record: InspectionRecord): boolean {
  return (
    inspectionHistoryStore
      .getBrakeTestsForRecord(record.id)
      .some((test) => isFailedResult(test.result)) ||
    inspectionHistoryStore
      .getLightingChecksForRecord(record.id)
      .some((check) => isFailedResult(check.result)) ||
    inspectionHistoryStore
      .getTyreTestsForRecord(record.id)
      .some((test) => isFailedResult(test.result)) ||
    inspectionHistoryStore
      .getVehicleConditionChecksForRecord(record.id)
      .some((check) => isFailedResult(check.result))
  );
}

/**
 * Used to highlight an inspection record's expansion panel title without
 * having to open it. Deliberately only looks at pass/fail results, not the
 * WOF/COF due-soon date threshold used elsewhere on this page - that's a
 * separate concern (compliance status) from whether an inspection's own
 * tests passed.
 *
 * "error": the record itself failed (its initial_result). This takes
 * priority over its individual tests - a record can fail overall for
 * reasons beyond a single failed test.
 *
 * "warning": the record passed overall, but at least one of its nested
 * tests failed.
 *
 * "none": the record passed and none of its tests failed.
 */
function getRecordResultState(
  record: InspectionRecord,
): "error" | "warning" | "none" {
  if (isFailedResult(record.initial_result)) return "error";
  if (hasFailedTest(record)) return "warning";
  return "none";
}

function getRecordResultStyle(record: InspectionRecord) {
  const state = getRecordResultState(record);
  if (state === "error") return failedItemStyle;
  if (state === "warning") return comingDueItemStyle;
  return undefined;
}

// Vuetify stores each theme colour as a bare "r, g, b" CSS variable (e.g.
// --v-theme-error), specifically so it can be dropped into rgb()/rgba() -
// used here to tint a failed list item's border/background without a
// separate "error20"-style fill token.
const failedItemStyle = {
  border: "1px solid rgb(var(--v-theme-error))",
  background: "rgba(var(--v-theme-error), 0.2)",
};

const comingDueItemStyle = {
  border: "1px solid rgb(var(--v-theme-warning))",
  background: "rgba(var(--v-theme-warning), 0.2)",
};

/**
 * Returns the source URL of the image associated with the vehicle.
 *
 * @return {string} The source URL of the image.
 */
function imageSrc(): string {
  if (vehicle) {
    // get uploaded image to car in first instance.
    if (vehicle.imageSrcPaths[0]) return vehicle.imageSrcPaths[0];
  }
  // get default image in fourth instance.
  return "/images/car_images/placeholder_car.png";
}

// event handlers

const userStore = useUserStore();

/**
 * Handles the selection of a vehicle by dispatching the selectVehicle action.
 *
 * @return {void} This function does not return a value.
 */
function handleSelect(): void {
  if (vehicle?.externalId) {
    userStore.setAndUpdatePrefs({
      key: "selectedVehicleExternalId",
      value: vehicle.externalId,
    });
  }
}

/**
 * Opens the vehicle gallery dialog so the user can pick a different default
 * trip vehicle than the one currently being viewed.
 *
 * @return {void} This function does not return a value.
 */
function handleSelectOther(): void {
  showVehicleGalleryDialog.value = true;
}

const showVehicleGalleryDialog = ref(false);
const showBookingDialog = ref(false);

async function handleVehicleSelect(selectedVehicle: Vehicle): Promise<void> {
  showVehicleGalleryDialog.value = false;
  await userStore.setAndUpdatePrefs({
    key: "selectedVehicleExternalId",
    value: selectedVehicle.externalId,
  });
}
</script>

<style scoped>
.svg_base {
  width: 56px;
  height: 56px;
}

.svg_selected {
  transform: scale(1.2);
}

.svg_unselected {
  opacity: 0.6;
}

/* Styled after #unread_notifications_bar in NotificationsContent.vue, using
   the theme's error token instead of a hardcoded info color. */
#wof_cof_expired_band,
#location_stale_band,
#ruc_overdue_band {
  background-color: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-error));
  padding: 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  height: 70px;
  margin-bottom: 20px;
  font-weight: 500;
}

/* Same shape as the error bands above, tinted with the warning token instead. */
#ruc_coming_due_band {
  background-color: rgba(var(--v-theme-warning), 0.12);
  color: rgb(var(--v-theme-warning));
  padding: 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  height: 70px;
  margin-bottom: 20px;
  font-weight: 500;
}

/* Expansion panel titles default to a plain chevron and Vuetify's stock
   opaque-gray hover overlay (background-color: currentColor at
   --v-hover-opacity) — swap both for the theme's primary tokens instead. */
.inspection-history-panels :deep(.v-expansion-panel-title__icon) {
  color: rgb(var(--v-theme-primary));
}

.inspection-history-panels
  :deep(.v-expansion-panel-title:hover > .v-expansion-panel-title__overlay) {
  background-color: rgb(var(--v-theme-primary));
  opacity: 0.15;
}
</style>
